import React, { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { AutoCompleteCompleteMethodParams } from 'primereact/autocomplete';
import scalesService from '../../service/api/ScalesService';
import suggestionsService from '../../service/api/SuggestionsService';
import FormErrorMessageScrollerRhf from '../_shared/FormErrorMessageScrollerRhf';
import { useTranslation } from 'react-i18next';
import { SuggestionOutput } from '../../types/Suggestion';
import { AutoCompleteField } from '../_shared/fields';

interface FormValues {
  packagedGoodsControlAutocomplete: SuggestionOutput | null;
}

const INIT_FORM_STATE: FormValues = {
  packagedGoodsControlAutocomplete: null,
};

type Props = {
  dialogVisible: boolean;
  closeModal: VoidFunction;
  scaleId: string;
  currentPackagedGoodsControl: { id: string; name: string } | null;
  onSuccess: (packagedGoodsControlName: string | null) => void;
};

function PackagedGoodsControlAssignmentDialog({
  dialogVisible,
  closeModal,
  scaleId,
  currentPackagedGoodsControl,
  onSuccess,
}: Props) {
  const { t } = useTranslation();

  const methods = useForm<FormValues>({
    defaultValues: INIT_FORM_STATE,
  });

  const { handleSubmit, reset, setError } = methods;

  const hideDialog = () => {
    reset(INIT_FORM_STATE);
    closeModal();
  };

  const [filteredPackagedGoodsControls, setFilteredPackagedGoodsControls] = useState<SuggestionOutput[]>([]);

  const searchPackagedGoodsControls = async (event: AutoCompleteCompleteMethodParams) => {
    const query = event.query;
    const suggestions = await suggestionsService.getSuggestions({ query, entity: 'packagedGoodsControls' });
    setFilteredPackagedGoodsControls(suggestions);
  };

  useEffect(() => {
    if (dialogVisible && currentPackagedGoodsControl) {
      reset({
        packagedGoodsControlAutocomplete: currentPackagedGoodsControl.id
          ? { id: currentPackagedGoodsControl.id, value: currentPackagedGoodsControl.name }
          : null,
      });
    } else {
      reset(INIT_FORM_STATE);
    }
  }, [dialogVisible, currentPackagedGoodsControl, reset]);

  const onSubmit = async (formData: FormValues) => {
    try {
      const packagedGoodsControlId = formData.packagedGoodsControlAutocomplete?.id || null;
      await scalesService.assignPackagedGoodsControl(scaleId, packagedGoodsControlId);

      const packagedGoodsControlName = formData.packagedGoodsControlAutocomplete?.value || null;
      onSuccess(packagedGoodsControlName);
      hideDialog();
    } catch (error: any) {
      if (error.response?.status === 409) {
        const field = error.response.data.field;
        setError('packagedGoodsControlAutocomplete', {
          type: 'manual',
          message: t(`scaleInfo.error_${field}_already_taken`),
        });
      } else {
        setError('packagedGoodsControlAutocomplete', {
          type: 'manual',
          message: t('common.error_occurred'),
        });
      }
    }
  };

  const DialogFooter = (
    <>
      <Button
        type="reset"
        label={t('common.cancel')}
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />
      <Button
        type="submit"
        label={t('common.save')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={handleSubmit(onSubmit)}
      />
    </>
  );

  return (
    <Dialog
      visible={dialogVisible}
      header={t('scaleInfo.assign_packaged_goods_control')}
      modal
      className="p-fluid"
      footer={DialogFooter}
      onHide={hideDialog}
      breakpoints={{ '1400px': '60vw', '896px': '90vw' }}
      style={{ width: '40vw' }}
    >
      <FormProvider {...methods}>
        <FormErrorMessageScrollerRhf>
          <div className="col-12">
            <AutoCompleteField
              containerClass=""
              inputName="packagedGoodsControlAutocomplete"
              inputLabel={t('scaleInfo.packaged_goods_control')}
              suggestions={filteredPackagedGoodsControls}
              field="value"
              dropdown
              completeMethod={searchPackagedGoodsControls}
              placeholder={t('common.select')}
            />
          </div>
        </FormErrorMessageScrollerRhf>
      </FormProvider>
    </Dialog>
  );
}

export default PackagedGoodsControlAssignmentDialog;
